/**
 * useFollowLatest — keeps a chat's scroll area at its end while an answer is
 * written, until the reader scrolls up (docs/specs/lookup.md).
 *
 * Following is decided by what the reader does, not by how close to the end
 * the view happens to be. It stops on the reader's own move up: a wheel or
 * trackpad turn up, a finger dragging the text down, a scroll key (PageUp,
 * ArrowUp, Home, Shift+Space) or a press on the scrollbar, and, for any other
 * input, a scroll up away from the end that the content shrinking does not
 * explain. It resumes
 * only when the reader scrolls back down to the very end, or on `jump`.
 *
 * The pin itself (`scrollTop = scrollHeight`) only ever moves down, so it
 * never changes the decision; nor does a near-the-end position, which is
 * what let each new chunk pull a reader who had just started scrolling up
 * back to the bottom.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';

const UP_KEYS = new Set(['PageUp', 'ArrowUp', 'Home']);

/** At the end, give or take a fractional pixel of a zoomed page. */
export const isAtEnd = (el: HTMLElement) => el.scrollHeight - el.scrollTop - el.clientHeight <= 1;

const isEditable = (target: EventTarget | null) =>
    target instanceof HTMLElement && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName));

export function useFollowLatest<T extends HTMLElement>(area: React.RefObject<T>) {
    const following = useRef(true);
    // False once the reader has scrolled up from the end: shows "Jump to latest".
    const [atLatest, setAtLatest] = useState(true);
    // The area's position at its last scroll, to tell the reader's moves from
    // the pin's and from content shrinking under them.
    const last = useRef({ top: 0, height: 0 });
    const touchY = useRef<number | null>(null);

    const setFollowing = useCallback((next: boolean) => {
        following.current = next;
        setAtLatest(next);
    }, []);
    const stop = useCallback(() => { if (following.current) setFollowing(false); }, [setFollowing]);

    const toEnd = useCallback(() => {
        const el = area.current;
        if (!el) return;
        el.scrollTop = el.scrollHeight;
        last.current = { top: el.scrollTop, height: el.scrollHeight };
    }, [area]);

    /** Keeps the end in view while following; call it whenever content grows. */
    const pin = useCallback(() => { if (following.current) toEnd(); }, [toEnd]);
    /** Follows again from the end ("Jump to latest", or a new question). */
    const jump = useCallback(() => { setFollowing(true); toEnd(); }, [setFollowing, toEnd]);

    // Scroll keys reach the area from inside it, or from the page when nothing
    // else has focus; typing in a field (the composer's ArrowUp) is not one.
    useEffect(() => {
        const key = (event: KeyboardEvent) => {
            if (!(UP_KEYS.has(event.key) || (event.key === ' ' && event.shiftKey)) || isEditable(event.target)) return;
            const el = area.current;
            if (el && (event.target === document.body || (event.target instanceof Node && el.contains(event.target)))) stop();
        };
        document.addEventListener('keydown', key);
        return () => document.removeEventListener('keydown', key);
    }, [area, stop]);

    const props = {
        onWheel: (event: React.WheelEvent) => { if (event.deltaY < 0) stop(); },
        onTouchStart: (event: React.TouchEvent) => { touchY.current = event.touches[0]?.clientY ?? null; },
        onTouchMove: (event: React.TouchEvent) => {
            const y = event.touches[0]?.clientY;
            if (y !== undefined && touchY.current !== null && y > touchY.current) stop();
            touchY.current = y ?? null;
        },
        // A press right of the area's content is on its scrollbar. (An overlay
        // scrollbar takes no width; dragging it is caught by onScroll below.)
        onPointerDown: (event: React.PointerEvent<HTMLElement>) => {
            const el = event.currentTarget;
            if (event.clientX >= el.getBoundingClientRect().left + el.clientLeft + el.clientWidth) stop();
        },
        onScroll: () => {
            const el = area.current;
            if (!el) return;
            const before = last.current;
            last.current = { top: el.scrollTop, height: el.scrollHeight };
            if (following.current) {
                // A move up that still ends at the end is the browser keeping
                // the view in range as the area grows ("Jump to latest" hiding
                // below it), not the reader; stopping on it would show the
                // button again, shrink the area, and loop.
                if (el.scrollTop < before.top && el.scrollHeight >= before.height && !isAtEnd(el)) setFollowing(false);
            } else if (el.scrollTop > before.top && isAtEnd(el)) {
                setFollowing(true);
            }
        },
    };

    return { following, atLatest, pin, jump, props };
}
